/**
 * SetNextSeqDialog — gated affordance for the Tally-migration use case.
 *
 * The dialog opens from the per-card "Continue from existing series"
 * button. It's only ever reachable when `canSetNextSeq` is true on the
 * row (server confirms the gate; this is just the UI surface).
 *
 * Save flow:
 *   1. Operator types the LAST issued number from their old system
 *      (e.g., 142). We add 1 in the wire payload — `nextSeq = 143`.
 *   2. Mutation fires `numbering.setNextSeq`. On success we invalidate
 *      `numbering.get` so the form picks up the new state.
 *   3. CONFLICT (`next-seq-locked`) maps to a field error and the
 *      dialog stays open with the message — race-only, shouldn't
 *      happen since we only opened it because the row was unissued.
 */
import * as React from "react";
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Input,
  Label,
  Alert,
} from "@flint/ui";

export interface SetNextSeqDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentType: string;
  /** Label shown in the dialog title (e.g., "Tax invoice"). */
  documentLabel: string;
  /** Preview-only — what the next number would format as. */
  formatPreview: (seq: number) => string;
  /** Server error (e.g., next-seq-locked) — kept inside the dialog. */
  serverError: string | null;
  /** Pending state from the mutation. */
  isPending: boolean;
  /** Submit handler — caller does the actual mutateAsync + invalidate. */
  onSubmit: (nextSeq: number) => void;
}

export function SetNextSeqDialog({
  open,
  onOpenChange,
  documentType,
  documentLabel,
  formatPreview,
  serverError,
  isPending,
  onSubmit,
}: SetNextSeqDialogProps) {
  const [lastIssuedRaw, setLastIssuedRaw] = React.useState("");

  // Reset state every time the dialog reopens for a different doc type.
  React.useEffect(() => {
    if (open) setLastIssuedRaw("");
  }, [open, documentType]);

  const parsed = parsePositiveInt(lastIssuedRaw);
  const nextSeq = parsed === null ? null : parsed + 1;
  const preview = nextSeq === null ? null : formatPreview(nextSeq);

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Set starting number — {documentLabel.toLowerCase()}</DialogTitle>
          <DialogDescription>
            Enter the LAST number you issued in your previous system. Flint will start one above
            that — so your numbering continues seamlessly.
          </DialogDescription>
        </DialogHeader>

        {serverError ? (
          <Alert intent="bad" title="Couldn't set starting number" description={serverError} />
        ) : null}

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="last-issued-seq">Last number issued in old system</Label>
          <Input
            id="last-issued-seq"
            value={lastIssuedRaw}
            inputMode="numeric"
            autoFocus
            placeholder="e.g. 142"
            className="font-mono"
            onChange={(e) => setLastIssuedRaw(e.target.value.replace(/\D/g, "").slice(0, 8))}
          />
          {preview ? (
            <p className="text-xs text-muted leading-relaxed">
              Next number in Flint will be <span className="font-mono">{preview}</span>.
            </p>
          ) : (
            <p className="text-xs text-muted leading-relaxed">
              Enter a positive integer. We&apos;ll add 1 — your next Flint number takes over from
              there.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" intent="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            intent="primary"
            disabled={nextSeq === null || isPending}
            onClick={() => nextSeq !== null && onSubmit(nextSeq)}
          >
            {isPending ? "Saving…" : "Set starting number"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}

function parsePositiveInt(raw: string): number | null {
  if (!raw.trim()) return null;
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1) return null;
  return n;
}
